import axios from "axios";

/**
 * Operations API Service
 * All API calls related to the Operations page
 */

const OperationsService = {
  /**
   * Get all zones (facilities) with substations (buildings) and load data
   */
  getZones: async () => {
    const response = await axios.get("/operations/zones");
    return response.data;
  },

  /**
   * Get specific building details with MSB readings
   * @param {number} buildingId - Building ID
   */
  getBuildingDetails: async (buildingId) => {
    const response = await axios.get(`/operations/building/${buildingId}`);
    return response.data;
  },

  /**
   * Update whether a zone (facility) allows automatic state changes by script
   * @param {number} zoneId - Zone (facility) ID
   * @param {boolean} canScriptChangeState - New value for can_script_change_state
   */
  updateZoneScriptState: async (zoneId, canScriptChangeState) => {
    const response = await axios.put("/operations/zones/" + zoneId, {
      can_script_change_state: !!canScriptChangeState,
    });
    return response.data;
  },

  /**
   * Get building power history for chart
   * @param {number} buildingId - Building ID
   * @param {number} hours - Number of hours of history (default: 6)
   */
  getBuildingPowerHistory: async (buildingId, hours = 6) => {
    const response = await axios.get(`/operations/building/${buildingId}/power-history`, {
      params: { hours }
    });
    return response.data;
  },

  /**
   * Get maintenance events for a building
   * @param {number} buildingId - Building ID
   */
  getBuildingMaintenanceEvents: async (buildingId) => {
    const response = await axios.get(`/operations/building/${buildingId}/maintenance-events`);
    return response.data;
  },

  /**
   * Create a maintenance event for a building
   * @param {number} buildingId - Building ID
   * @param {object} data - maintenance event payload
   */
  createBuildingMaintenanceEvent: async (buildingId, data) => {
    const response = await axios.post(`/operations/building/${buildingId}/maintenance-events`, data);
    return response.data;
  },

  /**
   * Update a maintenance event
   * @param {number} eventId - Maintenance event ID
   * @param {object} data - maintenance event payload
   */
  updateBuildingMaintenanceEvent: async (eventId, data) => {
    const response = await axios.put(`/operations/maintenance-events/${eventId}`, data);
    return response.data;
  },

  /**
   * Delete a maintenance event
   * @param {number} eventId - Maintenance event ID
   */
  deleteBuildingMaintenanceEvent: async (eventId) => {
    await axios.delete(`/operations/maintenance-events/${eventId}`);
  },

  /**
   * Trigger charge for building
   * @param {number} buildingId - Building ID
   * @param {object} data - { startChargeDateTime, chargeDuration }
   */
  triggerCharge: async (buildingId, data) => {
    const response = await axios.post(`/operations/building/${buildingId}/charge`, data);
    return response.data;
  },

  /**
   * Trigger discharge for building
   * @param {number} buildingId - Building ID
   * @param {object} data - { startDischargeDateTime, dischargeDuration, dischargeRate }
   */
  triggerDischarge: async (buildingId, data) => {
    const response = await axios.post(`/operations/building/${buildingId}/discharge`, data);
    return response.data;
  },

  /**
   * Trigger idle for building
   * @param {number} buildingId - Building ID
   * @param {object} data - { startIdleDateTime }
   */
  triggerIdle: async (buildingId, data) => {
    const response = await axios.post(`/operations/building/${buildingId}/idle`, data);
    return response.data;
  },

  /**
   * Trigger DLS reset for building
   * @param {number} buildingId - Building ID
   */
  triggerResetDLS: async (buildingId, data) => {
    const response = await axios.post(`/operations/building/${buildingId}/reset-dls`, data);
    return response.data;
  },

  /**
   * Get charge queue for building
   * @param {number} buildingId - Building ID
   */
  getBuildingChargeQueue: async (buildingId, thingName) => {
    const response = await axios.get(`/operations/building/${buildingId}/charge-queue`, {
      params: { thingName }
    });
    return response.data;
  },

  /**
   * Trigger discharge for multiple buildings at once
   * @param {number[]} buildingIds - Array of building IDs
   * @param {string} startDischargeDateTime - ISO datetime string (local time)
   * @param {number} [dischargeDuration=35] - Discharge duration in minutes
   * @param {number} [dischargeRate=-1] - Discharge rate in kW (-1 = full rate)
   */
  triggerBulkCharge: async (buildingIds, startDischargeDateTime, dischargeDuration = 35, dischargeRate = -1) => {
    const response = await axios.post('/operations/building/bulk-charge', {
      buildingIds,
      startDischargeDateTime,
      dischargeDuration,
      dischargeRate,
    });
    return response.data;
  }
};

export default OperationsService;
